/**
 * Bannière Mode Impersonation
 * P2.13 - Impersonation
 * Affichée en haut de toutes les pages quand l'impersonation est active
 *
 * ARCHITECTURE: Ce composant vérifie le rôle SUPERADMIN avant de faire
 * tout appel API. Il ne fait rien pour les utilisateurs non-superadmin.
 */

import React, { useEffect } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { useImpersonation } from '@/features/superadmin/hooks/useImpersonation';
import { useAuthStore } from '@/stores/authStore';
import { getAccessToken } from '@/services/tokenManager';

export const ImpersonationBanner: React.FC = () => {
  const { user, isAuthenticated } = useAuthStore();
  const { activeSession, stopImpersonation, isLoading, checkActiveImpersonation, isChecked } = useImpersonation();

  // Vérifier l'authentification ET le rôle SUPERADMIN avant tout appel API
  // On vérifie aussi qu'il y a un token valide pour éviter les appels API avec token expiré
  const hasValidToken = !!getAccessToken();
  const isSuperAdmin = isAuthenticated && hasValidToken && user?.role === 'SUPERADMIN';

  useEffect(() => {
    // Ne vérifier l'impersonation QUE si l'utilisateur est authentifié et SUPERADMIN
    if (isSuperAdmin && !isChecked) {
      checkActiveImpersonation();
    }
  }, [isSuperAdmin, isChecked, checkActiveImpersonation]);

  // Ne rien afficher si pas superadmin ou pas de session active
  if (!isSuperAdmin || !activeSession) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 animate-pulse" />
            <div>
              <p className="font-bold text-sm">
                MODE IMPERSONATION ACTIF
              </p>
              <p className="text-xs opacity-90">
                Connecté en tant que: <span className="font-semibold">{activeSession.targetUser.fullName}</span> ({activeSession.targetUser.email})
              </p>
            </div>
          </div>
          <button
            onClick={stopImpersonation}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-4 h-4" />
            Arrêter l'impersonation
          </button>
        </div>
      </div>
    </div>
  );
};
